<script setup>
import { ref, computed } from 'vue';
import { format } from 'date-fns';
import { useI18n } from 'vue-i18n';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  order: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const isExpanded = ref(false);

const formatDate = dateString => {
  if (!dateString) return '';
  try {
    return format(new Date(dateString), 'MMM d, yyyy');
  } catch (e) {
    return String(dateString);
  }
};

const formatCurrency = (amount, currency) => {
  if (amount === undefined || amount === null) return '';
  try {
    const validCurrency =
      currency && typeof currency === 'string' && currency.length === 3
        ? currency.toUpperCase()
        : 'USD';
    const num = Number(amount);
    if (isNaN(num)) return `${amount} ${currency || ''}`.trim();
    return new Intl.NumberFormat('en', {
      style: 'currency',
      currency: validCurrency,
    }).format(num);
  } catch (e) {
    return `${amount} ${currency || ''}`.trim();
  }
};

const orderTitle = computed(() => {
  if (props.order.name) return props.order.name;
  if (props.order.order_number) return `#${props.order.order_number}`;
  return t('CONVERSATION_SIDEBAR.SHOPIFY.ORDER_ID', { id: props.order.id });
});

const getStatusI18nKey = (type, status = '') => {
  return `CONVERSATION_SIDEBAR.SHOPIFY.${type.toUpperCase()}_STATUS.${String(status || '').toUpperCase()}`;
};

const financialStatus = computed(() => {
  const { financial_status: status } = props.order;
  if (!status) return '';
  const key = getStatusI18nKey('FINANCIAL', status);
  const translated = t(key);
  return translated && translated !== key ? translated : status.replace(/_/g, ' ');
});

const fulfillmentStatus = computed(() => {
  const { fulfillment_status: status } = props.order;
  if (!status) return '';
  const key = getStatusI18nKey('FULFILLMENT', status);
  const translated = t(key);
  return translated && translated !== key ? translated : status.replace(/_/g, ' ');
});

const getFinancialStatusClass = status => {
  const classes = {
    paid: 'bg-n-teal-4 text-n-teal-12 dark:bg-n-teal-12/20 dark:text-n-teal-9',
    partially_paid: 'bg-n-amber-4 text-n-amber-12 dark:bg-n-amber-12/20 dark:text-n-amber-9',
    authorized: 'bg-n-iris-4 text-n-iris-12 dark:bg-n-iris-12/20 dark:text-n-iris-9',
    pending: 'bg-n-amber-4 text-n-amber-12 dark:bg-n-amber-12/20 dark:text-n-amber-9',
    partially_refunded: 'bg-n-ruby-4 text-n-ruby-12 dark:bg-n-ruby-12/20 dark:text-n-ruby-9',
    refunded: 'bg-n-ruby-4 text-n-ruby-12 dark:bg-n-ruby-12/20 dark:text-n-ruby-9',
    voided: 'bg-n-slate-4 text-n-slate-12 dark:bg-n-slate-12/20 dark:text-n-slate-9',
  };
  return classes[status] || 'bg-n-slate-4 text-n-slate-12';
};

const getFulfillmentStatusClass = status => {
  const classes = {
    fulfilled: 'bg-n-teal-3 text-n-teal-11 dark:bg-n-teal-12/10 dark:text-n-teal-9',
    partially_fulfilled: 'bg-n-amber-3 text-n-amber-11 dark:bg-n-amber-12/10 dark:text-n-amber-9',
    unfulfilled: 'bg-n-ruby-3 text-n-ruby-11 dark:bg-n-ruby-12/10 dark:text-n-ruby-9',
  };
  return classes[status] || 'bg-n-slate-3 text-n-slate-11';
};

const lineItems = computed(() => {
  if (Array.isArray(props.order.line_items)) {
    return props.order.line_items;
  }
  return [];
});

const fulfillments = computed(() => {
  if (!Array.isArray(props.order.fulfillments)) return [];
  return props.order.fulfillments.filter(
    f => f && (f.tracking_number || (Array.isArray(f.tracking_numbers) && f.tracking_numbers.length))
  );
});

const shippingLocation = computed(() => {
  const addr = props.order.shipping_address;
  if (!addr || typeof addr !== 'object') return '';
  const parts = [
    addr.city,
    addr.province_code || addr.province,
    addr.country_code || addr.country,
  ].filter(Boolean);
  return parts.join(', ');
});

const copyTracking = async trackingNum => {
  if (!trackingNum) return;
  await copyTextToClipboard(trackingNum);
  useAlert(t('CONVERSATION_SIDEBAR.SHOPIFY.COPIED'));
};
</script>

<template>
  <div class="py-3 flex flex-col gap-2">
    <!-- Header: Order Name / Link and Status Badges -->
    <div class="flex items-center justify-between gap-2 min-w-0">
      <div class="font-medium text-sm flex items-center min-w-0 truncate">
        <a
          :href="order.admin_url"
          target="_blank"
          rel="noopener noreferrer"
          class="hover:underline text-n-slate-12 cursor-pointer truncate inline-flex items-center gap-1.5"
          :title="`Open ${orderTitle} in Shopify Admin`"
        >
          <span class="truncate font-semibold">{{ orderTitle }}</span>
          <i class="i-lucide-external-link flex-shrink-0 text-xs text-n-slate-10" />
        </a>
      </div>

      <div class="flex items-center gap-1.5 flex-shrink-0">
        <!-- Cancelled Badge -->
        <span
          v-if="order.cancelled_at"
          class="text-xxs px-1.5 py-0.5 rounded font-medium whitespace-nowrap bg-n-ruby-4 text-n-ruby-12"
          :title="order.cancel_reason ? `Cancelled: ${order.cancel_reason}` : $t('CONVERSATION_SIDEBAR.SHOPIFY.CANCELLED')"
        >
          {{ $t('CONVERSATION_SIDEBAR.SHOPIFY.CANCELLED') }}
        </span>

        <!-- Financial Status Badge -->
        <span
          v-if="financialStatus"
          :class="getFinancialStatusClass(order.financial_status)"
          class="text-xxs px-2 py-0.5 rounded font-medium whitespace-nowrap capitalize"
          :title="financialStatus"
        >
          {{ financialStatus }}
        </span>

        <!-- Fulfillment Status Badge -->
        <span
          v-if="fulfillmentStatus"
          :class="getFulfillmentStatusClass(order.fulfillment_status)"
          class="text-xxs px-2 py-0.5 rounded font-medium whitespace-nowrap capitalize"
          :title="fulfillmentStatus"
        >
          {{ fulfillmentStatus }}
        </span>
      </div>
    </div>

    <!-- Date & Price Row -->
    <div class="flex items-center gap-2 text-xs text-n-slate-11">
      <span>{{ formatDate(order.created_at) }}</span>
      <span>•</span>
      <span class="font-semibold text-n-slate-12">
        {{ formatCurrency(order.total_price, order.currency) }}
      </span>
      <span v-if="lineItems.length" class="text-n-slate-10">
        ({{ lineItems.length }} {{ lineItems.length === 1 ? 'item' : 'items' }})
      </span>
    </div>

    <!-- Tracking Info (WISMO) -->
    <div
      v-if="fulfillments.length"
      class="flex flex-col gap-1 text-xs rounded-md bg-n-slate-2 border border-n-weak p-2"
    >
      <div
        v-for="(fulfillment, idx) in fulfillments"
        :key="idx"
        class="flex items-center justify-between gap-1.5"
      >
        <div class="flex items-center gap-1.5 truncate text-n-slate-12 min-w-0">
          <i class="i-lucide-truck text-n-slate-10 flex-shrink-0 text-sm" />
          <span class="font-medium text-xxs uppercase tracking-wider text-n-slate-10 flex-shrink-0">
            {{ fulfillment.tracking_company || $t('CONVERSATION_SIDEBAR.SHOPIFY.TRACKING') }}:
          </span>
          <a
            v-if="fulfillment.tracking_url"
            :href="fulfillment.tracking_url"
            target="_blank"
            rel="noopener noreferrer"
            class="text-n-brand hover:underline font-mono text-xs truncate"
          >
            {{ fulfillment.tracking_number || (fulfillment.tracking_numbers && fulfillment.tracking_numbers[0]) || $t('CONVERSATION_SIDEBAR.SHOPIFY.TRACK_PACKAGE') }}
          </a>
          <span v-else class="font-mono text-xs truncate">
            {{ fulfillment.tracking_number || (fulfillment.tracking_numbers && fulfillment.tracking_numbers[0]) }}
          </span>
        </div>
        <button
          v-if="fulfillment.tracking_number || (fulfillment.tracking_numbers && fulfillment.tracking_numbers[0])"
          class="p-1 hover:bg-n-slate-4 rounded text-n-slate-10 hover:text-n-slate-12 flex-shrink-0 transition-colors cursor-pointer"
          title="Copy Tracking Number"
          @click.prevent="copyTracking(fulfillment.tracking_number || fulfillment.tracking_numbers[0])"
        >
          <i class="i-lucide-copy text-xs" />
        </button>
      </div>
    </div>

    <!-- Shipping Destination -->
    <div
      v-if="shippingLocation"
      class="flex items-center gap-1.5 text-xs text-n-slate-11 truncate"
    >
      <i class="i-lucide-map-pin text-n-slate-10 text-xs flex-shrink-0" />
      <span class="truncate">
        {{ $t('CONVERSATION_SIDEBAR.SHOPIFY.SHIPS_TO') }}: <span class="text-n-slate-12">{{ shippingLocation }}</span>
      </span>
    </div>

    <!-- Items Accordion / Toggle -->
    <div v-if="lineItems.length" class="mt-0.5">
      <button
        class="flex items-center justify-between w-full text-xxs font-medium text-n-slate-10 hover:text-n-slate-12 py-1 rounded transition-colors cursor-pointer"
        @click="isExpanded = !isExpanded"
      >
        <span class="flex items-center gap-1">
          <i class="i-lucide-package text-xs" />
          {{ $t('CONVERSATION_SIDEBAR.SHOPIFY.ITEMS', { count: lineItems.length }) }}
        </span>
        <i
          :class="isExpanded ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
          class="text-xs transition-transform"
        />
      </button>

      <!-- Expandable Line Items List -->
      <div
        v-if="isExpanded"
        class="flex flex-col gap-1.5 pt-1.5 pl-2 border-l-2 border-n-weak ml-1 text-xs"
      >
        <div
          v-for="item in lineItems"
          :key="item.id"
          class="flex items-start justify-between gap-2"
        >
          <div class="flex flex-col min-w-0">
            <span class="font-medium text-n-slate-12 truncate leading-tight">
              {{ item.quantity }}x {{ item.title }}
            </span>
            <span
              v-if="
                item.variant_title &&
                item.variant_title !== 'Default Title' &&
                item.variant_title !== item.title
              "
              class="text-xxs text-n-slate-10 truncate"
            >
              {{ item.variant_title }}
            </span>
          </div>
          <span class="text-n-slate-11 text-xxs font-mono flex-shrink-0">
            {{ formatCurrency(item.price, order.currency) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
